// Pure form and export rules for the module. No React, no fetch: the vitest gate pins these
// directly (H6 projection/correction bodies, H7 rate bodies and client checks, H9 CSV/chart).
import type { Component, FeedEntry, HistoryDetail, HistoryList, HistoryView, Marketplace, MarketplaceIn, Projection, ProjectionIn, RateIn, Rule } from "./api";
import { atHistoryStart, beforeHistoryStart, COPY, percentToFraction } from "./format";

export type MarketplaceDraft = {
  feeVat: string; vatStatus: "confirmed" | "pending"; dsfFba: string; dsfReferral: string; dsfStatus: "confirmed" | "pending";
  notes: string; active: boolean; reason: string;
};

export function projectionComponents(values: Partial<Record<Component, string>>, order: Component[]): ProjectionIn["components"] {
  return order.flatMap((name) => { const amount = values[name]; return amount && Number(amount) > 0 ? [{ component: name, value: amount }] : []; });
}

export function projectionReady(basis: string, components: ProjectionIn["components"]): boolean {
  return basis.trim().length > 0 && components.length > 0;
}

export function correctionIssues(value: string, reason: string, source: string): { value?: string; reason?: string; source?: string } {
  return {
    ...(Number(value) > 0 ? {} : { value: COPY.vFee }),
    ...(reason.trim() ? {} : { reason: COPY.vReason }),
    ...(source.trim() ? {} : { source: COPY.vSource }),
  };
}

// One entry per similar SKU, every billed component it carries, so a single button fills the whole form.
export function similarBySku(entries: FeedEntry[]): Array<{ xero_sku: string; currency: string; values: Partial<Record<Component, string>> }> {
  const bySku = new Map<string, { xero_sku: string; currency: string; values: Partial<Record<Component, string>> }>();
  for (const entry of entries) {
    const group = bySku.get(entry.xero_sku) ?? { xero_sku: entry.xero_sku, currency: entry.currency, values: {} };
    group.values[entry.component] = entry.value;
    bySku.set(entry.xero_sku, group);
  }
  return [...bySku.values()];
}

export function shownPercent(fraction: string | null): string { return fraction === null ? "" : String(Number(fraction) * 100); }

// Only the fields that differ from the stored row, plus the reason (H7). Null when nothing changed or a percent is invalid.
export function marketplaceBody(selected: Marketplace, draft: MarketplaceDraft): MarketplaceIn | null {
  if (!draft.reason.trim()) return null;
  const body: MarketplaceIn = { reason: draft.reason };
  const fee = percentToFraction(draft.feeVat);
  const dsfFba = percentToFraction(draft.dsfFba);
  const dsfReferral = percentToFraction(draft.dsfReferral);
  if (fee === null || dsfFba === null || dsfReferral === null) return null;
  if (draft.feeVat !== shownPercent(selected.fee_vat_rate)) body.fee_vat_rate = String(Number(fee));
  if (draft.vatStatus !== selected.vat_status) body.vat_status = draft.vatStatus;
  if (draft.dsfFba !== shownPercent(selected.dsf_pct_fba)) body.dsf_pct_fba = String(Number(dsfFba));
  if (draft.dsfReferral !== shownPercent(selected.dsf_pct_referral)) body.dsf_pct_referral = String(Number(dsfReferral));
  if (draft.dsfStatus !== selected.dsf_status) body.dsf_status = draft.dsfStatus;
  if (draft.notes !== (selected.notes ?? "")) body.notes = draft.notes;
  if (draft.active !== selected.active) body.active = draft.active;
  return Object.keys(body).length > 1 ? body : null;
}

// The client checks before PUT /v1/rates: a date that has arrived, or a change already scheduled for that date.
export function futureFeeIssue(future: RateIn, existing: Projection[], today: string): string | null {
  if (!future.effective_from || future.effective_from <= today) return COPY.pastDate;
  const sku = future.xero_sku ?? "";
  if (existing.some((row) => row.xero_sku === sku && row.marketplace_id === future.marketplace_id && row.component === future.component && row.effective_from === future.effective_from)) return COPY.duplicateFuture;
  return null;
}

export function ruleVersion(rules: Rule[], marketplaceId: string): number | null {
  return rules.find((rule) => rule.marketplace_id === marketplaceId && rule.component === "referral")?.version ?? null;
}

// The client check before PUT /v1/ceilings/{currency}: a value above zero and a reason.
export function ceilingIssue(value: string, reason: string): string | null {
  if (!(Number(value) > 0)) return COPY.vCeiling;
  if (!reason.trim()) return COPY.vReason;
  return null;
}

// Calendar days, the engine's `(today - effective_date).days`; no clock or time zone in the answer.
export function daysSince(date: string | null | undefined, today = new Date()): number | null {
  if (!date) return null;
  const [y, m, d] = date.slice(0, 10).split("-").map(Number);
  if (![y, m, d].every(Number.isFinite)) return null;
  const then = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((now - then) / 86_400_000));
}

// CSV of the visible table: one column per month in range, same order as the table (H9).
export function historyCsv(data: HistoryList, months: string[], view: HistoryView = "fee"): string {
  const head = ["SKU", "Product", "Variant", "Region", "Channel", "Location", "Currency", ...months];
  const lines = data.keys.map((key) => [key.xero_sku, key.product, key.variant, key.region, key.channel, key.location ?? "", key.currency ?? "", ...months.map((month) => {
    if (beforeHistoryStart(key.channel_location, month)) return "";
    if (view !== "fee" && atHistoryStart(key.channel_location, month)) return "";
    const value = key.months.find((entry) => entry.month === month);
    if (!value) return "";
    if (view === "change") return value.change ?? "";
    if (view === "pct") return value.change_pct ?? "";
    return value.per_unit ?? "";
  })]);
  return [head, ...lines].map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
}

// One point per month: the engine's per-unit total plus each component divided by units (the one division the spec allows).
export function chartRows(detail: HistoryDetail | undefined, components: Component[]): Array<Record<string, string | number | null>> {
  return (detail?.months ?? []).map((month) => ({
    month: month.month,
    total: month.per_unit === null ? null : Number(month.per_unit),
    ...Object.fromEntries(components.map((component) => [component, month.components[component] && month.units ? Number(month.components[component]) / month.units : null])),
  }));
}
